/**
 * Source-linked visual specifications for the warm-season runner specimens.
 *
 * These are intentionally data-only. A renderer may enlarge anatomy for
 * teaching, but must not present these as cultivar-accurate measurements or
 * as expert-reviewed 3D assets. Source coverage is strongest for traits a
 * homeowner can inspect at the collar, blade, runner, and seedhead.
 */

export type WarmRunnerModelKey =
  | 'bermudagrass'
  | 'zoysiagrass'
  | 'st-augustinegrass'
  | 'centipedegrass'

export type EvidenceLevel = 'source-stated' | 'figure-derived' | 'rendering-interpretation' | 'not-specified'

export interface WarmRunnerSource {
  id: string
  publisher: string
  title: string
  href: string
  supports: readonly string[]
}

export interface WarmRunnerModelSpec {
  key: WarmRunnerModelKey
  commonName: string
  scientificName: string
  taxonScope: 'species' | 'species-group'
  reviewStatus: 'expert-review-pending'
  reviewBoundary: string
  sources: readonly string[]
  blade: {
    vernation: 'folded' | 'rolled'
    width: {
      label: string
      millimeters?: readonly [number, number]
      evidence: EvidenceLevel
    }
    tip: 'pointed' | 'blunt-rounded'
    posture: 'prostrate' | 'arching' | 'upright'
    surface: string
    margin: string
    renderer: {
      widthScale: readonly [number, number]
      stiffness: 'soft' | 'moderate' | 'stiff'
      bladeCount: number
      tipTaper: 'sharp' | 'rounded'
    }
  }
  collar: {
    collarShape: string
    ligule: string
    auricles: 'absent'
    hairs: string
    renderer: {
      liguleStyle: 'hair-fringe' | 'membrane-with-cottony-hairs' | 'sparse-to-hairy-fringe'
      collarWidth: 'narrow' | 'variable' | 'broad'
      hairDensity: 'low' | 'medium' | 'high' | 'variable'
    }
  }
  runners: {
    stolons: {
      present: true
      relativeThickness: 'slender' | 'variable' | 'stout'
      nodeSpacing: string
      rootsAtNodes: true
      evidence: EvidenceLevel
    }
    rhizomes: {
      present: boolean
      relativeThickness?: 'slender' | 'variable' | 'stout'
      evidence: EvidenceLevel
    }
    renderer: {
      aboveGroundRunnerCount: number
      belowGroundRunnerCount: number
      internodeRhythm: 'variable' | 'uniform' | 'neutral'
    }
  }
  roots: {
    form: 'fibrous'
    depthCue: 'shallow' | 'not-species-diagnostic'
    nodeOrigin: boolean
    evidence: EvidenceLevel
    rendererNote: string
  }
  seedhead: {
    architecture: string
    seasonalVisibility: 'occasional in unmown turf' | 'often suppressed by mowing'
    renderer: {
      form: 'digitate-branches' | 'single-spike' | 'compact-spikelet-axis'
      branchCount?: readonly [number, number]
      spikeletArrangement: string
    }
  }
  uncertainty: readonly string[]
}

export const WARM_RUNNER_MODEL_SOURCES: readonly WarmRunnerSource[] = [
  {
    id: 'uf-ifas-turfgrass-identification',
    publisher: 'University of Florida IFAS Extension',
    title: 'Florida Turfgrass Identification',
    href: 'https://ask.ifas.ufl.edu/publication/EP646',
    supports: [
      'Bermudagrass, zoysiagrass, St. Augustinegrass, and centipedegrass identification tables',
      'vernation, blade, tip, sheath, collar, ligule, auricle, seedhead, and growth-habit comparisons',
    ],
  },
  {
    id: 'uf-ifas-bermudagrass-production',
    publisher: 'University of Florida IFAS Extension',
    title: 'Bermudagrass Production in Florida',
    href: 'https://ask.ifas.ufl.edu/publication/AA200',
    supports: [
      'Cynodon dactylon blade width range',
      'rhizomes and stolons',
      'digitally arranged cluster of three to five thin spikelets',
      'cultivar variability warning',
    ],
  },
  {
    id: 'uf-ifas-bermudagrass-seedlings',
    publisher: 'University of Florida IFAS Extension',
    title: 'Identification of Grass Weed Seedlings Commonly Found in Florida Sugarcane',
    href: 'https://ask.ifas.ufl.edu/publication/AG470',
    supports: ['Bermudagrass fibrous roots', 'rooting at stolon and rhizome nodes', 'hairy ligule'],
  },
  {
    id: 'uf-ifas-zoysiagrass-lawns',
    publisher: 'University of Florida IFAS Extension',
    title: 'Zoysiagrass for Florida Lawns',
    href: 'https://ask.ifas.ufl.edu/publication/LH011',
    supports: ['Zoysia species spread through rhizomes and stolons', 'dense mat and thatch context'],
  },
  {
    id: 'uf-ifas-st-augustinegrass-lawns',
    publisher: 'University of Florida IFAS Extension',
    title: 'St. Augustinegrass for Florida Lawns',
    href: 'https://ask.ifas.ufl.edu/publication/LH010',
    supports: ['stolon nodes generate shoots and roots', 'St. Augustinegrass is vegetatively propagated'],
  },
  {
    id: 'uf-ifas-centipedegrass-lawns',
    publisher: 'University of Florida IFAS Extension',
    title: 'Centipedegrass for Florida Lawns',
    href: 'https://ask.ifas.ufl.edu/publication/LH009',
    supports: ['3 to 5 mm leaf width', 'smooth blade with hairy basal margins', 'stolons root at nodes', 'shallow-rooted context'],
  },
] as const

export const WARM_RUNNER_MODEL_SPECS: Record<WarmRunnerModelKey, WarmRunnerModelSpec> = {
  bermudagrass: {
    key: 'bermudagrass',
    commonName: 'Bermudagrass',
    scientificName: 'Cynodon dactylon',
    taxonScope: 'species',
    reviewStatus: 'expert-review-pending',
    reviewBoundary: 'This is a species-level teaching model. Common and hybrid bermudagrasses vary in blade texture, stem size, internode length, and seed production.',
    sources: ['uf-ifas-turfgrass-identification', 'uf-ifas-bermudagrass-production', 'uf-ifas-bermudagrass-seedlings'],
    blade: {
      vernation: 'folded',
      width: { label: 'Very fine to coarse by species and cultivar; common bermudagrass is commonly 1.6 to 3.2 mm wide.', millimeters: [1.6, 3.2], evidence: 'source-stated' },
      tip: 'pointed',
      posture: 'prostrate',
      surface: 'Smooth to sparsely hairy sheath and blade surfaces. Leaf margins are slightly rough.',
      margin: 'Slightly rough, not a soft fringe.',
      renderer: { widthScale: [0.2, 0.42], stiffness: 'moderate', bladeCount: 22, tipTaper: 'sharp' },
    },
    collar: {
      collarShape: 'Narrow collar.',
      ligule: 'Fringe of hairs.',
      auricles: 'absent',
      hairs: 'Longer hairs can be conspicuous at the collar, but density varies among turf types.',
      renderer: { liguleStyle: 'hair-fringe', collarWidth: 'narrow', hairDensity: 'high' },
    },
    runners: {
      stolons: { present: true, relativeThickness: 'variable', nodeSpacing: 'Uneven, variable-length leaf spacing is a published comparison cue.', rootsAtNodes: true, evidence: 'source-stated' },
      rhizomes: { present: true, relativeThickness: 'variable', evidence: 'source-stated' },
      renderer: { aboveGroundRunnerCount: 6, belowGroundRunnerCount: 5, internodeRhythm: 'variable' },
    },
    roots: {
      form: 'fibrous',
      depthCue: 'not-species-diagnostic',
      nodeOrigin: true,
      evidence: 'source-stated',
      rendererNote: 'Render fine roots from both crown and runner nodes. Do not encode depth as a diagnostic cue without a cultivar and soil context.',
    },
    seedhead: {
      architecture: 'A terminal, finger-like cluster of three to five slender spike branches.',
      seasonalVisibility: 'often suppressed by mowing',
      renderer: { form: 'digitate-branches', branchCount: [3, 5], spikeletArrangement: 'Tiny spikelets arranged along each thin branch.' },
    },
    uncertainty: [
      'UF IFAS describes meaningful blade, stem, internode, and seed-production variation among cultivars.',
      'A seedling guide describes rolled bud leaves while the mature turf identification guide describes folded vernation. Treat vernation as supporting evidence, not the sole rendered diagnostic.',
      'Hybrid turf types can produce few or no viable seeds, so a seedhead is an optional teaching state rather than a required lawn observation.',
    ],
  },
  zoysiagrass: {
    key: 'zoysiagrass',
    commonName: 'Zoysiagrass',
    scientificName: 'Zoysia spp.',
    taxonScope: 'species-group',
    reviewStatus: 'expert-review-pending',
    reviewBoundary: 'Zoysia is a species group, not one plant. The renderer must show shared traits and must not imply a cultivar-level identification.',
    sources: ['uf-ifas-turfgrass-identification', 'uf-ifas-zoysiagrass-lawns'],
    blade: {
      vernation: 'rolled',
      width: { label: 'Very fine to coarse depending on species and cultivar.', evidence: 'source-stated' },
      tip: 'pointed',
      posture: 'upright',
      surface: 'Stiff blades that can carry fine hairs on the upper surface.',
      margin: 'Do not render a universal leaf-margin hair cue because it varies across Zoysia species and cultivars.',
      renderer: { widthScale: [0.16, 0.64], stiffness: 'stiff', bladeCount: 26, tipTaper: 'sharp' },
    },
    collar: {
      collarShape: 'Broad to narrow by species. Some Zoysia have long collar hairs.',
      ligule: 'Fringe of hairs.',
      auricles: 'absent',
      hairs: 'Upper-blade and collar hairs are conditional, not universal.',
      renderer: { liguleStyle: 'hair-fringe', collarWidth: 'variable', hairDensity: 'variable' },
    },
    runners: {
      stolons: { present: true, relativeThickness: 'variable', nodeSpacing: 'Internodes are generally more uniform than bermudagrass.', rootsAtNodes: true, evidence: 'source-stated' },
      rhizomes: { present: true, relativeThickness: 'variable', evidence: 'source-stated' },
      renderer: { aboveGroundRunnerCount: 5, belowGroundRunnerCount: 5, internodeRhythm: 'uniform' },
    },
    roots: {
      form: 'fibrous',
      depthCue: 'shallow',
      nodeOrigin: true,
      evidence: 'rendering-interpretation',
      rendererNote: 'UF IFAS describes zoysiagrass as shallower rooted in a lawn comparison and confirms runners make new root and shoot systems. Render a dense, shallow fibrous mat, but do not use root depth to identify a specimen.',
    },
    seedhead: {
      architecture: 'A compact spikelet-bearing seedhead, rendered as one narrow axis rather than a bermudagrass-like finger cluster.',
      seasonalVisibility: 'often suppressed by mowing',
      renderer: { form: 'compact-spikelet-axis', spikeletArrangement: 'Compact spikelets on a single narrow upright axis.' },
    },
    uncertainty: [
      'UF IFAS explicitly reports variation from very fine to coarse blades, variable collar width, and hairs only in some Zoysia.',
      'Use a selectable morphology range or cultivar profile before committing to one blade width, hair density, or runner thickness.',
      'The root rendering is a non-diagnostic teaching interpretation, not a cultivar measurement.',
    ],
  },
  'st-augustinegrass': {
    key: 'st-augustinegrass',
    commonName: 'St. Augustinegrass',
    scientificName: 'Stenotaphrum secundatum',
    taxonScope: 'species',
    reviewStatus: 'expert-review-pending',
    reviewBoundary: 'This teaching model prioritizes the distinctive broad, blunt blade and stolon-only growth habit. It is not a cultivar-accurate Raleigh, Floratam, Palmetto, or Seville model.',
    sources: ['uf-ifas-turfgrass-identification', 'uf-ifas-st-augustinegrass-lawns'],
    blade: {
      vernation: 'folded',
      width: { label: 'Coarse textured with a broad blade.', evidence: 'source-stated' },
      tip: 'blunt-rounded',
      posture: 'arching',
      surface: 'Flattened, smooth sheath and coarse, broad blade.',
      margin: 'No diagnostic hair fringe is specified for the blade margin.',
      renderer: { widthScale: [0.7, 1], stiffness: 'soft', bladeCount: 14, tipTaper: 'rounded' },
    },
    collar: {
      collarShape: 'Constricted collar with a twist and broad leaf blade and sheath.',
      ligule: 'Few hairs to a fringe of hairs.',
      auricles: 'absent',
      hairs: 'Keep the ligule sparse to moderately fringed rather than rendering auricles.',
      renderer: { liguleStyle: 'sparse-to-hairy-fringe', collarWidth: 'broad', hairDensity: 'medium' },
    },
    runners: {
      stolons: { present: true, relativeThickness: 'stout', nodeSpacing: 'Node spacing is visually interpreted from the UF IFAS stolon figure and must be reviewed against a specimen reference before final 3D signoff.', rootsAtNodes: true, evidence: 'figure-derived' },
      rhizomes: { present: false, evidence: 'source-stated' },
      renderer: { aboveGroundRunnerCount: 4, belowGroundRunnerCount: 0, internodeRhythm: 'neutral' },
    },
    roots: {
      form: 'fibrous',
      depthCue: 'not-species-diagnostic',
      nodeOrigin: true,
      evidence: 'source-stated',
      rendererNote: 'Render fibrous roots emerging beneath contact nodes because UF IFAS confirms stolon nodes generate new root and shoot growth. Avoid a depth comparison claim.',
    },
    seedhead: {
      architecture: 'A single spike seedhead.',
      seasonalVisibility: 'often suppressed by mowing',
      renderer: { form: 'single-spike', spikeletArrangement: 'Compact spikelets on one upright, unbranched axis.' },
    },
    uncertainty: [
      'Stolon thickness and node spacing need direct specimen-photo review. They are figure-derived visual parameters, not source-stated measurements.',
      'Cultivars can differ materially in canopy color, density, leaf width, and stress response.',
      'Mown home lawns may not expose the spike seedhead.',
    ],
  },
  centipedegrass: {
    key: 'centipedegrass',
    commonName: 'Centipedegrass',
    scientificName: 'Eremochloa ophiuroides',
    taxonScope: 'species',
    reviewStatus: 'expert-review-pending',
    reviewBoundary: 'This is a species-level teaching model with enlarged collar and runner anatomy. It does not encode cultivar-level color or exact node spacing.',
    sources: ['uf-ifas-turfgrass-identification', 'uf-ifas-centipedegrass-lawns'],
    blade: {
      vernation: 'folded',
      width: { label: 'Medium textured, 3 to 5 mm wide in the UF IFAS lawn profile.', millimeters: [3, 5], evidence: 'source-stated' },
      tip: 'blunt-rounded',
      posture: 'prostrate',
      surface: 'Smooth blade with hairs on the margin toward the base.',
      margin: 'Hairy toward the blade base, not uniformly hairy to the tip.',
      renderer: { widthScale: [0.42, 0.58], stiffness: 'moderate', bladeCount: 18, tipTaper: 'rounded' },
    },
    collar: {
      collarShape: 'Constricted with a broad blade and sheath; collar has hairs.',
      ligule: 'Membranous with cottony hairs.',
      auricles: 'absent',
      hairs: 'Cottony ligule hairs plus basal margin hairs are key close-up cues.',
      renderer: { liguleStyle: 'membrane-with-cottony-hairs', collarWidth: 'broad', hairDensity: 'high' },
    },
    runners: {
      stolons: { present: true, relativeThickness: 'slender', nodeSpacing: 'Low, slow-growing stolons root at nodes. Exact node spacing is not source-specified and should remain visually neutral.', rootsAtNodes: true, evidence: 'source-stated' },
      rhizomes: { present: false, evidence: 'source-stated' },
      renderer: { aboveGroundRunnerCount: 5, belowGroundRunnerCount: 0, internodeRhythm: 'neutral' },
    },
    roots: {
      form: 'fibrous',
      depthCue: 'shallow',
      nodeOrigin: true,
      evidence: 'source-stated',
      rendererNote: 'Render a relatively shallow, fibrous root fan at contact nodes. Its shallow-rooted context is educational but is not enough to confirm the species.',
    },
    seedhead: {
      architecture: 'A single spike seedhead.',
      seasonalVisibility: 'occasional in unmown turf',
      renderer: { form: 'single-spike', spikeletArrangement: 'Compact spikelets arranged on one upright axis.' },
    },
    uncertainty: [
      'Node spacing and stolon diameter are not source-stated measurements, so the renderer must not turn them into a species-confirming cue.',
      'Leaf color is naturally lighter than many lawn grasses but varies with fertility and iron status, so it should not drive model identity.',
      'A seedhead may be absent from regularly mown turf.',
    ],
  },
}
